import { __ } from "@wordpress/i18n";
import { useBlockProps, InnerBlocks } from "@wordpress/block-editor";
import { useState, useEffect } from "@wordpress/element";
import { useDispatch, useSelect } from "@wordpress/data";
import ShareInspectorControls from "./InspectorControls";
import { LinkIcon, SOCIAL_ICON_MAP } from "./social-icons";

const TRIGGER_TEMPLATE = [
  [
    "brandy/button",
    {
      tagName: "button",
      type: "button",
      text: "Share",
      icon: { enabled: true, name: "share-network", type: "regular", position: "left", gap: "8px" },
    },
  ],
];

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({ className: "brandy-share-post" });
  const [showPopupInEditor, setShowPopupInEditor] = useState(true);

  const { displayMode = "popup", enableCopyLink = true, copyLinkText = "Copy link", socials = [], popupStyles = {} } = attributes;

  const enabledSocials = socials.filter((s) => s.enabled);
  const hasSocials = enabledSocials.length > 0;

  const { removeBlocks } = useDispatch("core/block-editor");
  const innerBlocks = useSelect(
    (select) => select("core/block-editor").getBlock(clientId)?.innerBlocks ?? [],
    [clientId]
  );

  // Remove trigger button when switching to inline mode.
  useEffect(() => {
    if (displayMode === "inline" && innerBlocks.length > 0) {
      removeBlocks(innerBlocks.map((b) => b.clientId));
    }
  }, [displayMode]);

  const popupStyle = {
    display: "flex",
    flexDirection: "column",
    background: popupStyles.background || "#ffffff",
    borderRadius: popupStyles.borderRadius || "12px",
    border: `${popupStyles.borderWidth || "1px"} ${popupStyles.borderStyle || "solid"} ${popupStyles.borderColor || "#e2e8f0"}`,
    boxShadow: popupStyles.shadow || "0 4px 24px rgba(0,0,0,0.12)",
    "--brandy-text-color": popupStyles.textColor || "#374151",
    "--brandy-hover-bg": popupStyles.hoverBackground || "#f9fafb",
    "--brandy-hover-text-color": popupStyles.hoverTextColor || "#111827",
    "--brandy-item-padding": popupStyles.itemPadding || "12px",
    marginTop: "8px",
    minWidth: "220px",
    overflow: "hidden",
  };

  const ShareItems = ({ inline = false }) => (
    <>
      {enableCopyLink && (
        <div className="brandy-share-item brandy-copy-link" style={{ cursor: "default" }}>
          <span className="brandy-share-icon-wrap"><LinkIcon /></span>
          {!inline && <span>{copyLinkText}</span>}
        </div>
      )}
      {!inline && enableCopyLink && hasSocials && <hr className="brandy-share-divider" />}
      {enabledSocials.map((social) => (
        <div key={social.id} className={`brandy-share-item brandy-share-${social.id}`} style={{ cursor: "default" }}>
          <span className="brandy-share-icon-wrap">{SOCIAL_ICON_MAP[social.id]}</span>
          {!inline && <span>{social.text}</span>}
        </div>
      ))}
    </>
  );

  return (
    <div {...blockProps}>
      <ShareInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
        showPopupInEditor={showPopupInEditor}
        setShowPopupInEditor={setShowPopupInEditor}
      />

      {displayMode === "popup" ? (
        <div className="brandy-share-editor-preview">
          <div className="brandy-share-wrapper">
            <div className="brandy-share-trigger-wrapper">
              <InnerBlocks
                template={TRIGGER_TEMPLATE}
                templateLock={false}
                allowedBlocks={["brandy/button"]}
              />
            </div>
            {showPopupInEditor && (
              <div className="brandy-share-popup-preview" style={popupStyle}>
                <ShareItems inline={false} />
              </div>
            )}
          </div>
          {!showPopupInEditor && (
            <span className="brandy-share-editor-note">
              {__("Popup hidden in editor", "brandy-blocks")}
            </span>
          )}
        </div>
      ) : (
        <div className="brandy-share-inline">
          <ShareItems inline={true} />
        </div>
      )}
    </div>
  );
}
